/**
 * Map Firebase callable errors to stable reason strings for ErrorScreen.
 */
import { SESSION_TIMEOUT_CODE } from '@/services/auth';

interface FirebaseLike {
  code?: string;
  message?: string;
  name?: string;
}

export interface RunActionFailure {
  /** A retry can only fail the same way — drop the queued action instead. */
  permanent: boolean;
  message: string;
}

const TRANSIENT_SEND_MESSAGE = 'Could not save this action. Check your connection and retry.';

function isOffline(e: FirebaseLike): boolean {
  if (e.code === SESSION_TIMEOUT_CODE) return true;
  if (e.name === 'TypeError' && /fetch|network/i.test(e.message ?? '')) return true;
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/**
 * Split an advanceRunStep failure into "try again later" and "this will never
 * work". Every failure used to read as a connection problem, so a run the
 * server refused outright (2026-09-09: executor phone not accepted by
 * advanceRunStep) looked like bad WiFi and was retried forever.
 */
export function classifyRunActionError(err: unknown): RunActionFailure {
  if (!err || typeof err !== 'object') {
    return { permanent: false, message: TRANSIENT_SEND_MESSAGE };
  }
  const e = err as FirebaseLike;
  if (isOffline(e)) return { permanent: false, message: TRANSIENT_SEND_MESSAGE };

  switch (e.code) {
    case 'functions/permission-denied':
      return {
        permanent: true,
        message: 'This run is not assigned to your number. Ask whoever sent it to dispatch it again.',
      };
    case 'functions/unauthenticated':
      return {
        permanent: true,
        message: 'Your session expired. Reload the page to sign in again.',
      };
    case 'functions/not-found':
      return { permanent: true, message: 'This run no longer exists.' };
    case 'functions/failed-precondition':
    case 'functions/invalid-argument':
      // The reducer's own wording is more useful than anything generic.
      return {
        permanent: true,
        message: e.message?.trim() || 'This action is no longer possible. Reload the page.',
      };
    default:
      return { permanent: false, message: TRANSIENT_SEND_MESSAGE };
  }
}

export function classifyCallableError(err: unknown): string {
  if (!err || typeof err !== 'object') return 'network_error';
  const e = err as FirebaseLike;

  // The sign-in handshake gave up before it could even issue the call.
  if (e.code === SESSION_TIMEOUT_CODE) return 'timeout';

  if (e.name === 'TypeError' && /fetch|network/i.test(e.message ?? '')) {
    return 'network_error';
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return 'network_error';
  }

  const code = typeof e.code === 'string' ? e.code : '';
  if (code.startsWith('functions/')) {
    switch (code) {
      // Raised when our explicit callable timeout fires.
      case 'functions/deadline-exceeded':
        return 'timeout';
      case 'functions/unavailable':
      case 'functions/not-found':
        return 'network_error';
      case 'functions/failed-precondition':
        if (/app[- ]?check/i.test(e.message ?? '')) return 'app_check_failed';
        return 'network_error';
      default:
        return 'network_error';
    }
  }

  return 'network_error';
}
